import { useEffect, useMemo, useRef } from "react";

// Notices when a backend run a page was watching has ENDED. The polling itself is
// not here: it belongs to the query that carries the liveness - `useLeadsPipelineStatus`
// re-reads itself while any chain is running, and a landlord-research run is watched
// through its own operation row (`useOperations`) until that row goes terminal - so
// there is one polling idiom on these pages, react-query's `refetchInterval`, rather
// than a page-level interval beside it.
//
// What is left is the one thing a query cannot tell a page by itself: that a ref it
// reported live on the previous read is no longer live on this one. A ref the server
// confirmed as running and now omits fires `onFinished`, so the page can pull whatever
// that run wrote (the inbox re-reads the lead behind the row).
export const useLiveRuns = ({
  liveRefs,
  onFinished,
}: {
  // Refs the SERVER currently reports work in flight for.
  liveRefs: ReadonlySet<string>;
  // One ref's run has ended - pull anything keyed off that row.
  onFinished: (ref: string) => void;
}): void => {
  // The callback and the live set are rebuilt every render, so the effect keys on
  // membership changing, not on identity.
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;
  const liveRefsRef = useRef(liveRefs);
  liveRefsRef.current = liveRefs;
  const liveKey = useMemo(
    () => Array.from(liveRefs).sort().join(" "),
    [liveRefs]
  );
  // Refs the server has confirmed as running, so "it finished" can be told apart
  // from "it was never live". Nothing renders from it, so it stays a ref.
  const confirmedRefs = useRef<Set<string>>(new Set());

  useEffect(() => {
    const live = liveRefsRef.current;
    const confirmed = confirmedRefs.current;
    const finished: string[] = [];
    confirmed.forEach((ref) => {
      if (!live.has(ref)) finished.push(ref);
    });
    finished.forEach((ref) => confirmed.delete(ref));
    live.forEach((ref) => confirmed.add(ref));
    finished.forEach((ref) => onFinishedRef.current(ref));
  }, [liveKey]);
};
